<template>
  <VanPopup
    :show="show"
    @update:show="emit('update:show', $event)"
    close-on-click-overlay
    round
    class="max-h-[60%]"
  >
    <div v-if="showGainTitle" class="text-lg text-center font-bold pt-4">
      {{ $t("Message.getThing") }}！
    </div>
    <div v-if="equipment" class="flex flex-col rounded-md p-4 gap-4">
      <!-- Item header with icon -->
      <div class="flex items-start gap-3">
        <div class="w-12 h-12 shrink-0">
          <EquipmentIcon :equipment="equipment" show-lock />
        </div>
        <div class="flex-1">
          <h3 :class="['font-bold text-lg', `rarity-${equipment?.rarity}`]">
            {{ equipment.name }}
            <span class="text-sm text-primary/50" v-if="equipment.isLocked">
              ({{ $t("Message.hasLocked") }})
            </span>
          </h3>
          <p class="text-sm">
            <span class="text-base-content/50">
              {{ $t(`mission.equipment.rarity.name`) }}:
            </span>
            <span :class="[`rarity-${equipment?.rarity}`]">
              {{ $t(`mission.equipment.rarity.${equipment.rarity}`) }}
            </span>
          </p>
          <p
            class="text-sm text-base-content/50 mt-1"
            v-if="mode === 'edit' && equipment.dispatch"
          >
            <span> {{ $t(`mission.equipment.firstDiscovery`) }}:</span>
            <RouterLink class="underline" :to="`/mate/${equipment.mate?.uuid}`">
              {{ equipment.mate?.name }}
            </RouterLink>
            <span class="mx-1">在</span>

            <span class="mx-1">
              {{
                [
                  equipment.dispatch?.location?.worldview?.name,
                  equipment.dispatch?.location?.name,
                ].join("-")
              }}
              {{ $t("Message.s") }}
              {{ equipment.dispatch?.mission?.name }}，
            </span>

            <RouterLink
              class="underline"
              :to="`/mission/journey/item?dispatchUuid=${equipment.dispatch?.uuid}`"
            >
              {{ $t(`mission.title`) }}ID #{{ equipment.dispatch?.id }}
            </RouterLink>
          </p>
        </div>
      </div>

      <!-- 描述 -->
      <div>
        <h4 class="font-bold mb-2">
          {{ $t(`mission.equipment.description`) }}
        </h4>
        <p class="text-sm">{{ equipment.description }}</p>
      </div>

      <!-- 效果 -->
      <div>
        <h4 class="font-bold mb-2">
          {{ $t(`mission.equipment.effect`) }}
        </h4>
        <p class="text-sm">{{ equipment.effect }}</p>
      </div>

      <!-- 任务增益 -->
      <div
        :class="[`rarity-${equipment?.rarity}`]"
        v-if="equipment.type === EquipmentType.Item"
      >
        <h4 class="font-bold mb-2">
          {{ $t(`mission.equipment.missionSuccessRate`) }}:

          <span class="text-sm">
            +{{ fixNumber(rarityMap[equipment.rarity], 100) }}%
          </span>
        </h4>
      </div>

      <!-- Bottom button -->
      <div class="flex justify-end gap-2" v-if="mode === 'edit'">
        <template v-if="equipment.type === EquipmentType.Item">
          <button class="btn btn-sm btn-outline" @click="clickLock">
            <i
              :class="[
                'iconify',
                equipment.isLocked
                  ? 'mingcute--unlock-line'
                  : 'mingcute--lock-line',
              ]"
            ></i>
            {{
              $t(`mission.equipment.${equipment.isLocked ? "unlock" : "lock"}`)
            }}
          </button>

          <button class="btn btn-sm btn-outline" @click="clickRecycle">
            <i class="iconify mingcute--delete-2-line"></i>
            {{ $t(`mission.equipment.recycle`) }}
          </button>
        </template>

        <template v-if="equipment.type === EquipmentType.Chest">
          <button class="btn btn-sm btn-primary" @click="clickOpenChest">
            {{ $t(`Buttons.open`) }}
          </button>

          <button
            v-if="showGainTitle"
            class="btn btn-sm"
            @click="emit('update:show', false)"
          >
            {{ $t(`mission.equipment.putInBox`) }}
          </button>
        </template>
      </div>

      <div class="mt-4 flex justify-end" v-if="mode === 'select'">
        <button class="btn btn-sm btn-outline" @click="clickSelect">
          {{ isSelect ? $t(`Buttons.unselect`) : $t(`Buttons.select`) }}
        </button>
      </div>
    </div>
  </VanPopup>
</template>

<script setup lang="ts">
import { MateEquipment } from "@/types/entity";
import { showConfirmDialog, showSuccessToast } from "vant";
import { useI18n } from "vue-i18n";
import { useMission } from "@/stores/mission";
import { fixNumber, rarityMap } from "./MissionStat";
import { EquipmentType } from "@/enums/mission";
import { useGlobalModal } from "@/stores/globalModal";

const props = defineProps<{
  show: boolean;
  equipment?: MateEquipment;
  mode: "select" | "edit" | "view";
  isSelect?: boolean;
  showGainTitle?: boolean;
}>();

const { t } = useI18n();
const missionStore = useMission();
const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
  (e: "select"): void;
}>();
const modalStore = useGlobalModal();

const clickRecycle = () => {
  showConfirmDialog({
    message: t("mission.equipment.recycleConfirm"),
    confirmButtonText: t("Buttons.confirm"),
    cancelButtonText: t("Buttons.cancel"),
  }).then(async () => {
    await missionStore.decomposeMateEquipment(props.equipment!.id);
    showSuccessToast(t("mission.equipment.recycleSuccess"));
    emit("update:show", false);
  });
};

const clickLock = async () => {
  const { isLocked } = props.equipment!;
  const message = isLocked
    ? t("mission.equipment.unlockConfirm")
    : t("mission.equipment.lockConfirm");

  showConfirmDialog({
    message,
    confirmButtonText: t("Buttons.confirm"),
    cancelButtonText: t("Buttons.cancel"),
  }).then(async () => {
    await missionStore.putMateEquipment(props.equipment!.id, {
      isLocked: !props.equipment!.isLocked,
    });

    showSuccessToast(
      t(`mission.equipment.${isLocked ? "unlockSuccess" : "lockSuccess"}`)
    );
    props.equipment!.isLocked = !props.equipment!.isLocked;
    emit("update:show", false);
  });
};

const clickOpenChest = async () => {
  const res = await missionStore.openMateEquipmentChest(props.equipment!.id);
  modalStore.openReceivedModal(
    [
      {
        amount: res.powerCubes,
        type: "powerCube",
      },
    ],
    "equipmentDecompose"
  );
  emit("update:show", false);
};

const clickSelect = () => {
  emit("select");
};
</script>
